import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Link,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import axiosInstant from "../../../api";
import {
    getProductCategories,
    searchBrand,
    searchUnbrandedProducts,
    adminRmnPromoteProductEndpoint,
    adminRmnPromoteBrandEndpoint,
} from "../../../api/endpoints";
import { apiErrorCode } from "../../../utils/apiError";
import { RmnDiscoveredBrand, RmnDiscoveredProduct } from "../../../types/rmn";
import {
    CategoryOption,
    groupedCategoryOptions,
} from "../../network/discoveredImportShared";

interface MatchOption {
    id: number;
    label: string;
    image?: string | null;
}

type PromoteMatchModalProps = {
    onClose: () => void;
    onPromoted: () => void;
} & (
    | { mode: "product"; record: RmnDiscoveredProduct }
    | { mode: "brand"; record: RmnDiscoveredBrand }
);

// Promotion review: the staged (crawled) record on the left, canonical-catalog
// search on the right. Picking a result links the staged row to it; "Create
// new" promotes with an empty body so the API creates a fresh canonical record.
export function PromoteMatchModal(props: PromoteMatchModalProps) {
    const { onClose, onPromoted } = props;
    const isProduct = props.mode === "product";
    const noun = isProduct ? "product" : "brand";
    const stagedName = isProduct
        ? props.record.title || props.record.url
        : props.record.name;
    const stagedImage = isProduct ? props.record.image : props.record.logo;
    const stagedUrl = props.record.url;

    const [query, setQuery] = useState(stagedName || "");
    const [options, setOptions] = useState<MatchOption[]>([]);
    const [searching, setSearching] = useState(false);
    const [promoting, setPromoting] = useState(false);
    const [inlineError, setInlineError] = useState<string | null>(null);

    // Promotes must land categorized (the API 400s CategoryRequired
    // otherwise) — pre-filled from the crawl passes' identification.
    const [categories, setCategories] = useState<CategoryOption[]>([]);
    const [categoryId, setCategoryId] = useState<number | "">(
        props.mode === "product" && props.record.product_category_id
            ? props.record.product_category_id
            : ""
    );

    useEffect(() => {
        if (!isProduct) return undefined;
        let stale = false;
        axiosInstant
            .get(getProductCategories())
            .then((res) => {
                if (!stale) setCategories((res.data || []) as CategoryOption[]);
            })
            .catch(() => {
                if (!stale) setCategories([]);
            });
        return () => {
            stale = true;
        };
    }, [isProduct]);

    // Search-as-you-type against the canonical catalog (debounced).
    useEffect(() => {
        let stale = false;
        const q = query.trim();
        if (!q) {
            setOptions([]);
            return undefined;
        }
        const timer = window.setTimeout(async () => {
            setSearching(true);
            try {
                const endpoint = isProduct
                    ? searchUnbrandedProducts(encodeURIComponent(q))
                    : searchBrand(encodeURIComponent(q));
                const res = await axiosInstant.get(endpoint);
                if (stale) return;
                const rows = (res.data && res.data.rows) || [];
                setOptions(
                    rows.slice(0, 10).map((row: any) => ({
                        id: row.id,
                        label: isProduct ? row.title : row.name,
                        image: isProduct ? row.image : row.logo,
                    }))
                );
            } catch (err) {
                if (!stale) setOptions([]);
            } finally {
                if (!stale) setSearching(false);
            }
        }, 300);
        return () => {
            stale = true;
            window.clearTimeout(timer);
        };
    }, [query, isProduct]);

    const promote = async (matchId?: number) => {
        setPromoting(true);
        setInlineError(null);
        try {
            const endpoint = isProduct
                ? adminRmnPromoteProductEndpoint(props.record.id)
                : adminRmnPromoteBrandEndpoint(props.record.id);
            let body: Record<string, unknown> = {};
            if (matchId) {
                body = isProduct ? { desire_id: matchId } : { brand_id: matchId };
            }
            if (isProduct && categoryId) body.product_category_id = categoryId;
            await axiosInstant.post(endpoint, body);
            toast.success(
                matchId
                    ? "Imported to TDC — linked to existing record"
                    : "Imported to TDC — new record created"
            );
            onPromoted();
        } catch (err) {
            const code = apiErrorCode(err);
            if (code === "CategoryRequired") {
                setInlineError("Choose a TDC category to import this product.");
            } else if (code === "AlreadyPromoted") {
                toast.success("Already imported — refreshing the list.");
                onPromoted();
            } else {
                toast.error("Failed to import");
            }
        } finally {
            setPromoting(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="md" onClose={() => !promoting && onClose()}>
            <DialogTitle>
                <div className="flex justify-between items-center">
                    <Typography variant="h5" className="text-[20px]">
                        Import {noun} To TDC
                    </Typography>
                    <IconButton onClick={onClose} disabled={promoting}>
                        <CloseIcon className="text-text-2" />
                    </IconButton>
                </div>
            </DialogTitle>
            <DialogContent>
                <Box className="flex flex-col md:flex-row gap-6">
                    {/* Staged record */}
                    <Box className="flex-1 min-w-0">
                        <Typography variant="subtitle2" gutterBottom>
                            Discovered record
                        </Typography>
                        <Box className="flex gap-3 p-3 rounded-lg border border-border-token bg-surface-2">
                            {stagedImage && (
                                <Box
                                    component="img"
                                    src={stagedImage}
                                    alt=""
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        objectFit: "contain",
                                        borderRadius: 1,
                                        // stays white in both themes: plate behind
                                        // third-party product/logo art (often dark
                                        // line art on transparency)
                                        bgcolor: "var(--surface)",
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                            <Stack spacing={0.5} className="min-w-0">
                                <Typography sx={{ fontWeight: 600 }} noWrap>
                                    {stagedName}
                                </Typography>
                                {stagedUrl && (
                                    <Link
                                        href={stagedUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        variant="caption"
                                        noWrap
                                    >
                                        {stagedUrl}
                                    </Link>
                                )}
                                {props.mode === "product" && props.record.price && (
                                    <Typography variant="body2">
                                        {props.record.price}
                                    </Typography>
                                )}
                                {props.mode === "product" && props.record.brand_text && (
                                    <Typography variant="caption" color="text.secondary">
                                        Brand: {props.record.brand_text}
                                    </Typography>
                                )}
                                {props.mode === "product" && props.record.rmn_category && (
                                    <Typography variant="caption" color="text.secondary">
                                        Category: {props.record.rmn_category.name}
                                    </Typography>
                                )}
                            </Stack>
                        </Box>

                        {isProduct && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    TDC category (required to import)
                                </Typography>
                                <select
                                    value={categoryId}
                                    disabled={promoting}
                                    onChange={(e) => {
                                        setInlineError(null);
                                        setCategoryId(
                                            e.target.value ? Number(e.target.value) : ""
                                        );
                                    }}
                                    className="w-full px-3 py-2 text-[14px] border border-border-token rounded-lg bg-surface cursor-pointer"
                                >
                                    <option value="">Select a category…</option>
                                    {groupedCategoryOptions(categories)}
                                </select>
                            </Box>
                        )}

                        {inlineError && (
                            <Typography variant="body2" sx={{ color: "var(--down)", mt: 1.5 }}>
                                {inlineError}
                            </Typography>
                        )}
                    </Box>

                    <Divider orientation="vertical" flexItem className="hidden md:block" />

                    {/* Canonical search */}
                    <Box className="flex-1 min-w-0">
                        <Typography variant="subtitle2" gutterBottom>
                            Match to existing {noun}
                        </Typography>
                        <TextField
                            fullWidth
                            autoFocus
                            size="small"
                            placeholder={isProduct ? "Search products" : "Search brands"}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            InputProps={{
                                endAdornment: searching ? (
                                    <CircularProgress size={16} />
                                ) : undefined,
                            }}
                        />
                        <Stack
                            spacing={1}
                            sx={{ mt: 1.5, maxHeight: 280, overflowY: "auto" }}
                        >
                            {options.map((opt) => (
                                <Box
                                    key={opt.id}
                                    className="flex items-center justify-between gap-2 p-2 rounded-lg border border-border-token"
                                >
                                    <Box className="flex items-center gap-2 min-w-0">
                                        {opt.image && (
                                            <Box
                                                component="img"
                                                src={opt.image}
                                                alt=""
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    objectFit: "contain",
                                                    borderRadius: 0.5,
                                                    flexShrink: 0,
                                                }}
                                            />
                                        )}
                                        <Typography variant="body2" noWrap>
                                            {opt.label}
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={promoting}
                                        onClick={() => promote(opt.id)}
                                    >
                                        Select
                                    </Button>
                                </Box>
                            ))}
                            {!searching && query.trim() && options.length === 0 && (
                                <Typography variant="caption" color="text.secondary">
                                    No matches — import as a new record instead.
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions className="!px-6 !py-4">
                <Button variant="outlined" disabled={promoting} onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={promoting || (isProduct && !categoryId)}
                    onClick={() => promote()}
                >
                    Create new {noun}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
